import React, { useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronUp, Star, CheckCircle } from 'lucide-react';
import { faqData, categories, categoryDescriptions } from '../data/faq';

export default function FAQ() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const filteredFAQs = faqData.filter((faq) => {
    const matchesSearch =
      searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const featuredFAQs = faqData.filter((faq) => faq.featured);

  // Tooltip map for common acronyms; keeps content readable for non-technical users
  const tooltipMap = useMemo(
    () =>
      new Map<string, string>([
        ['PII', 'Personally Identifiable Information'],
        ['TLS', 'Transport Layer Security (HTTPS encryption)'],
        ['CSP', 'Content Security Policy (browser security headers)'],
        ['RBAC', 'Role-Based Access Control'],
        ['RPO', 'Recovery Point Objective (data loss window target)'],
        ['RTO', 'Recovery Time Objective (restore-time target)'],
        ['SLA', 'Service Level Agreement'],
        ['SLO', 'Service Level Objective'],
        ['KYC', 'Know Your Customer (identity verification)'],
        ['AML', 'Anti-Money Laundering compliance'],
        ['USDC', 'USD Coin (a U.S. dollar-pegged digital asset)'],
        ['CSV', 'Comma-Separated Values (spreadsheet export format)'],
        ['API', 'Application Programming Interface'],
      ]),
    [],
  );

  const addTooltips = (html: string, showIcons = false) => {
    for (const [abbr, title] of tooltipMap.entries()) {
      // Match acronym not preceded by an alphanumeric, and not followed by an alphanumeric
      const re = new RegExp('(^|[^A-Za-z0-9])(' + abbr + ')(?![A-Za-z0-9])', 'g');
      const icon = showIcons
        ? '<svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" class="inline-block ml-1 align-text-top text-slate-400"><path fill="currentColor" d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2m.75 15h-1.5v-6h1.5Zm0-8h-1.5V6h1.5Z"/></svg>'
        : '';
      html = html.replace(
        re,
        '$1<abbr title="' + title + '" class="border-b border-dotted cursor-help">$2</abbr>' + icon,
      );
    }
    return html;
  };

  const linkify = (html: string) => {
    // Convert plain URLs into clickable links
    return html.replace(
      /(https?:\/\/[^\s)]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-700">$1</a>',
    );
  };

  // Format multi-line answers; basic markdown-like bold, URL linkify, acronym tooltips, and preserved line breaks
  const formatAnswer = (text: string, opts?: { showAcronymIcons?: boolean }) => {
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const withBold = escaped.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    const withLinks = linkify(withBold);
    const withTooltips = addTooltips(withLinks, !!opts?.showAcronymIcons);
    const html = withTooltips
      .split('\n\n')
      .map(function (p) {
        return '<p class="mb-3 leading-relaxed">' + p.replace(/\n/g, '<br/>') + '</p>';
      })
      .join('');
    return { __html: html } as const;
  };

  return (
    <section className="mx-auto max-w-4xl px-4 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
          Get answers about ToolboxLogic's community app platform - a complete community operating
          system for modern HOAs and community associations
        </p>
      </div>

      {/* Category description when a specific filter is active */}
      {selectedCategory !== 'all' && (
        <div className="mb-8 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-start gap-3">
            {(() => {
              const cat = categories[selectedCategory as keyof typeof categories];
              if (!cat) return null;
              const Icon = cat.icon;
              return (
                <div className="mt-0.5">
                  <Icon className={'h-5 w-5 ' + cat.color} />
                </div>
              );
            })()}
            <div>
              <div className="font-semibold text-slate-900">
                {categories[selectedCategory as keyof typeof categories]?.title}
              </div>
              <div className="text-slate-600">
                {categoryDescriptions[selectedCategory as keyof typeof categoryDescriptions]}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-8 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search frequently asked questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-900 placeholder-slate-500"
          />
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={
              'px-4 py-2 rounded-full text-sm font-medium transition-colors ' +
              (selectedCategory === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200')
            }
          >
            All Questions
          </button>
          {Object.entries(categories).map(([key, category]) => {
            const IconComponent = category.icon;
            return (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={
                  'px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ' +
                  (selectedCategory === key
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200')
                }
              >
                <IconComponent className="h-4 w-4" />
                {category.title}
              </button>
            );
          })}
        </div>
      </div>

      {/* Featured Questions (when no search/filter) */}
      {searchQuery === '' && selectedCategory === 'all' && (
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-6">
            <Star className="h-6 w-6 text-amber-500 fill-current" />
            <h2 className="text-2xl font-bold text-slate-900">Most Important Questions</h2>
          </div>
          <div className="grid gap-4">
            {featuredFAQs.map((faq) => {
              const category = categories[faq.category as keyof typeof categories];
              const IconComponent = category.icon;
              return (
                <div
                  key={faq.id}
                  className="bg-linear-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0">
                      <div className={'p-2 rounded-lg bg-white'}>
                        <IconComponent className={'h-5 w-5 ' + category.color} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">{faq.question}</h3>
                      <div
                        className="text-slate-700 leading-relaxed"
                        dangerouslySetInnerHTML={formatAnswer(faq.answer, {
                          showAcronymIcons: ['security', 'data'].includes(faq.category),
                        })}
                      />
                      <div className="mt-3">
                        <span
                          className={
                            'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ' +
                            category.color +
                            ' bg-white'
                          }
                        >
                          {category.title}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All Questions */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-6">
          {searchQuery || selectedCategory !== 'all' ? 'Search Results' : 'All Questions'}
          <span className="text-lg font-normal text-slate-500 ml-2">({filteredFAQs.length})</span>
        </h2>

        {filteredFAQs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500 text-lg">No questions found matching your search.</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear search and show all questions
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFAQs.map((faq) => {
              const category = categories[faq.category as keyof typeof categories];
              const IconComponent = category.icon;
              const isExpanded = expandedItems.has(faq.id);

              return (
                <div key={faq.id} className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleExpanded(faq.id)}
                    className="w-full px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="shrink-0 mt-1">
                          <IconComponent className={'h-5 w-5 ' + category.color} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-slate-900 pr-4">
                            {faq.question}
                          </h3>
                          {faq.featured && (
                            <div className="mt-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-amber-700 bg-amber-100">
                                <Star className="h-3 w-3 mr-1 fill-current" />
                                Popular
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0">
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-6 pb-4">
                      <div className="pl-8">
                        <div
                          className="text-slate-700 leading-relaxed"
                          dangerouslySetInnerHTML={formatAnswer(faq.answer, {
                            showAcronymIcons: ['security', 'data'].includes(faq.category),
                          })}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="mt-16 bg-linear-to-r from-blue-600 to-indigo-600 rounded-xl p-8 text-center text-white">
        <h3 className="text-2xl font-bold mb-4">Ready to see how it works for your community?</h3>
        <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
          Schedule a personalized demo to see exactly how the community app platform will transform
          your community operations and resident engagement.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="/schedule-demo"
            className="bg-white text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-lg font-semibold transition-colors inline-flex items-center justify-center"
          >
            <CheckCircle className="h-5 w-5 mr-2" />
            Schedule Demo
          </a>
          <a
            href="/pricing"
            className="border border-blue-300 text-white hover:bg-blue-700 px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            View Pricing Details
          </a>
        </div>
      </div>
    </section>
  );
}
